import React from 'react';
import verticalFrame from '../../assets/comabooks-frames/pdfvertical.png';
import squareFrame from '../../assets/comabooks-frames/pdfsquare.png';
import horizontalFrame from '../../assets/comabooks-frames/pdfhorizontal.png';
import { ComabooksPhotoStatus, isFramedStatus } from './comabooksPhoto';
import { CHANGE, CROP_Y, CX, LAYOUT_H, LAYOUT_W, pt, topFromBaseline, u } from './geometry';
import { ComabooksFooter } from './ComabooksFooter';
import './ComabooksPage.css';

type FramedStatus = 'vertical' | 'square' | 'horizontal';
const FRAME_SRC: Record<FramedStatus, string> = {
  vertical: verticalFrame,
  square: squareFrame,
  horizontal: horizontalFrame,
};
const FRAME_RATIOS: Record<FramedStatus, number> = { horizontal: 0.5263, square: 0.8896, vertical: 1.4627 };
const FRAME_W = 762;

export interface ComabooksPhotoPageProps {
  status: ComabooksPhotoStatus;
  photoUrl: string;
  /** Уже отформатированная дата (дд.мм.гггг); пусто — не показывается. */
  date?: string;
  description?: string;
  showDate?: boolean;
  showDescription?: boolean;
  footerTitle?: string;
  footerAuthor?: string;
  pageNumber?: number;
  safeZone?: boolean;
  className?: string;
}

/**
 * Фото-страница Comabooks 1:1 с печатью (pdfGeneratorComabooks.ts drawPhotoPage):
 *  top/center/bottom — фото на всю страницу, затемнение 20%, белые дата
 *  (Playfair 42pt) и подпись (Playfair Italic 42pt) по центру на y 200/851/1100;
 *  vertical/square/horizontal — фото в рамке 762 px, чёрные дата и подпись
 *  (Playfair Bold) сверху на y 130/170.
 */
export const ComabooksPhotoPage: React.FC<ComabooksPhotoPageProps> = ({
  status,
  photoUrl,
  date = '',
  description = '',
  showDate = true,
  showDescription = true,
  footerTitle = '',
  footerAuthor = '',
  pageNumber,
  safeZone = false,
  className = '',
}) => {
  const framed = isFramedStatus(status);
  const hasContent = showDate || showDescription;
  const dateText = showDate ? date : '';
  const desc = showDescription ? description : '';
  const y = status === 'top' ? 200 : status === 'bottom' ? 1100 : status === 'center' ? 851 : 130;
  const dateY = (framed ? 130 : y) + CHANGE;
  const descY = (framed ? (dateText ? 170 : 130) : dateText ? y + 80 : y) + CHANGE;

  let frameBox: React.CSSProperties | undefined;
  if (framed) {
    const fh = FRAME_W * FRAME_RATIOS[status as FramedStatus];
    frameBox = {
      left: u((LAYOUT_W - FRAME_W) / 2),
      top: u((LAYOUT_H - fh) / 2 - CROP_Y),
      width: u(FRAME_W),
      height: u(fh),
    };
  }

  return (
    <div className={`cbp ${framed ? 'cbp--dark' : 'cbp--light'} ${className}`}>
      <div className="cbp__sheet">
        {!photoUrl && <div className="cbp__empty">фото не выбрано</div>}
        {photoUrl && !framed && <img className="cbp__photo" src={photoUrl} alt="" />}
        {photoUrl && !framed && hasContent && <div className="cbp__shade" />}
        {photoUrl && framed && <img className="cbp__frame-photo" src={photoUrl} alt="" style={frameBox} />}
        {framed && (
          <img
            className="cbp__frame-overlay"
            src={FRAME_SRC[status as FramedStatus]}
            alt=""
            style={{ top: u(-CROP_Y), height: u(LAYOUT_H) }}
          />
        )}
        {dateText && (
          <div
            className="cbp__center-text"
            style={{ left: u(CX), top: topFromBaseline(dateY, 42), fontSize: pt(42), whiteSpace: 'nowrap' }}
          >
            {dateText}
          </div>
        )}
        {desc && (
          <div
            className={`cbp__center-text ${framed ? 'cbp__desc--bold' : 'cbp__desc--italic'}`}
            style={{ left: u(CX), top: topFromBaseline(descY, 42), fontSize: pt(42), width: u(668) }}
          >
            {desc}
          </div>
        )}
        <ComabooksFooter title={footerTitle} author={footerAuthor} pageNumber={pageNumber} />
        {safeZone && <div className="cbp__bleed" aria-hidden />}
      </div>
    </div>
  );
};
